import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import { Plus, XCircle, RefreshCw, Search, ArrowLeft, Eraser, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowUpDown, Pencil, Trash2, Info, Eye, HelpCircle, X, Zap, UserCog, ArrowRight, Mail, Monitor } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useIncidentList, useCreateIncident, useCloseIncident, useUpdateIncident, useDeleteIncident } from '../hooks/useIncidents.ts';
import { useActiveTenants } from '@/shared/hooks/useTenantsSearch.ts';
import { TenantCombobox } from '@/shared/components/TenantCombobox.tsx';
import { CloseIncidentModal } from './CloseIncidentModal.tsx';
import { TenantMultiSelect } from '@/shared/components/TenantMultiSelect.tsx';
import { RowActionMenu, type RowAction } from '@/shared/components/RowActionMenu.tsx';
import { ConfirmModal } from '@/shared/components/ConfirmModal.tsx';
import type { IncidentRow, IncidentListParams } from '@/shared/types/incident.ts';
import type { CreateIncidentDTO, UpdateIncidentDTO } from '../api/incidents.api.ts';
import { DateOnlyPicker } from '@/shared/components/DateTimePicker.tsx';

type StatusFilter = 'all' | 'open' | 'closed';
type ViewMode = 'list' | 'form';

const columnHelper = createColumnHelper<IncidentRow>();

const SEVERIDADE_COLORS: Record<string, string> = {
  CRITICA: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  ALTA: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  MEDIA: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
};

const SEVERIDADE_LABELS: Record<string, string> = {
  CRITICA: 'Crítica',
  ALTA: 'Alta',
  MEDIA: 'Média',
};

const TIPO_LABELS: Record<string, string> = {
  FALHA: 'Falha',
  DEGRADACAO: 'Degradação',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function calcDuration(start: string, end: string | null): string {
  const endDate = end ? new Date(end) : new Date();
  const diffMs = endDate.getTime() - new Date(start).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) return `${hrs}h ${remMins}min`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

// ── Form schema ─────────────────────────────────────────
const formSchema = z.object({
  titulo: z.string().min(2, 'Título deve ter no mínimo 2 caracteres'),
  descricao: z.string().optional(),
  severidade: z.enum(['CRITICA', 'ALTA', 'MEDIA']),
  tipo: z.enum(['FALHA', 'DEGRADACAO']),
});

type FormFields = z.infer<typeof formSchema>;

// ── Tooltip component ──────────────────────────────────────
function FieldTooltip({ title, subtitle, details, tip }: {
  title: string;
  subtitle: string;
  details: { text: string; bold?: string }[];
  tip: string;
}) {
  return (
    <div className="relative ml-1 inline-block">
      <span className="group cursor-help text-gray-400 hover:text-blue-500 transition-colors">
        <Info size={15} />
        <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-[300px] -translate-x-1/2 rounded-xl border border-gray-200 bg-white opacity-0 shadow-xl transition-all group-hover:pointer-events-auto group-hover:opacity-100 dark:border-gray-700 dark:bg-gray-900 overflow-hidden">
          <div className="flex items-start gap-3 px-4 py-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
              <Info size={18} />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
            </div>
          </div>
          <div className="space-y-2 px-4 pb-3">
            {details.map((d, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
                <Info size={13} className="mt-0.5 flex-shrink-0 text-blue-500" />
                <span>{d.bold ? <><strong className="text-gray-800 dark:text-gray-100">{d.bold}</strong> {d.text}</> : d.text}</span>
              </div>
            ))}
          </div>
          <div className="flex items-start gap-2 border-t border-gray-100 bg-blue-50 px-4 py-2.5 dark:border-gray-800 dark:bg-blue-900/20">
            <span className="text-xs">💡</span>
            <p className="text-xs text-blue-700 dark:text-blue-300">{tip}</p>
          </div>
        </div>
      </span>
    </div>
  );
}

// ── Causa Raiz tooltip ──────────────────────────────────────
function CausaRaizTooltip({ causaRaiz }: { causaRaiz: string }) {
  return (
    <div className="group relative inline-block">
      <span className="cursor-help text-gray-400 hover:text-blue-500 transition-colors">
        <Info size={14} />
      </span>
      <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-[280px] -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-3 opacity-0 shadow-xl transition-all group-hover:pointer-events-auto group-hover:opacity-100 dark:border-gray-700 dark:bg-gray-900">
        <p className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300">Causa Raiz</p>
        <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{causaRaiz}</p>
        <div className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900" />
      </div>
    </div>
  );
}

// ── Modal: Como funciona a abertura de incidentes ────────────
function IncidentHelpModal({ onClose }: { onClose: () => void }) {
  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative mx-4 w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
              <HelpCircle size={18} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Como funcionam os incidentes?</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* Abertura automática */}
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800/50 dark:bg-orange-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Zap size={16} className="text-orange-600 dark:text-orange-400" />
              <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300">Abertura Automática</h4>
            </div>
            <div className="space-y-2 text-xs text-orange-700 dark:text-orange-300/90">
              <p>O worker monitora todos os tenants a cada <strong>1 minuto</strong> e utiliza o mecanismo <strong>anti-flap</strong> para evitar falsos positivos:</p>
              <div className="flex items-center gap-1.5 rounded-md bg-orange-100 px-3 py-2 font-mono text-[11px] dark:bg-orange-900/30">
                <span className="rounded bg-red-200 px-1.5 py-0.5 text-red-700 dark:bg-red-900/50 dark:text-red-300">DOWN</span>
                <ArrowRight size={12} />
                <span className="rounded bg-red-200 px-1.5 py-0.5 text-red-700 dark:bg-red-900/50 dark:text-red-300">DOWN</span>
                <ArrowRight size={12} />
                <span className="rounded bg-red-200 px-1.5 py-0.5 text-red-700 dark:bg-red-900/50 dark:text-red-300">DOWN</span>
                <ArrowRight size={12} />
                <span className="rounded bg-red-600 px-1.5 py-0.5 text-white">INCIDENTE</span>
              </div>
              <ul className="ml-4 list-disc space-y-1">
                <li><strong>3 checks DOWN consecutivos</strong> abrem o incidente automaticamente</li>
                <li><strong>1 check UP</strong> encerra o incidente imediatamente</li>
                <li>Se houver 2 DOWNs e depois 1 UP, o contador reseta (sem incidente)</li>
              </ul>
            </div>
          </div>

          {/* O que é DOWN */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">O que é considerado DOWN?</h4>
            <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
              <p><strong className="text-red-600 dark:text-red-400">DOWN:</strong> timeout, connection refused, falha DNS, erro de rede</p>
              <p><strong className="text-green-600 dark:text-green-400">UP:</strong> qualquer resposta HTTP (mesmo 4xx/5xx = servidor respondeu)</p>
              <p className="text-[11px] italic text-gray-500">Cada probe tem retry automático com timeout estendido antes de confirmar DOWN.</p>
            </div>
          </div>

          {/* Abertura manual */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/50 dark:bg-blue-900/20">
            <div className="mb-2 flex items-center gap-2">
              <UserCog size={16} className="text-blue-600 dark:text-blue-400" />
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">Abertura Manual</h4>
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-300/90">
              Você também pode criar incidentes manualmente pelo botão <strong>"Novo Incidente"</strong>, selecionando os tenants afetados, severidade e tipo. Útil para registrar degradações percebidas pelos usuários.
            </p>
          </div>

          {/* Notificações */}
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800/50 dark:bg-purple-900/20">
            <div className="mb-2 flex items-center gap-2">
              <Mail size={16} className="text-purple-600 dark:text-purple-400" />
              <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300">Notificações</h4>
            </div>
            <p className="text-xs text-purple-700 dark:text-purple-300/90">
              Quando um incidente é aberto — automático ou manual — um <strong>email de alerta</strong> é enviado automaticamente para os responsáveis configurados.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

export function IncidentListPage() {
  // ── State ─────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showHelp, setShowHelp] = useState(false);
  const [editingIncident, setEditingIncident] = useState<IncidentRow | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [tenantFilter, setTenantFilter] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'iniciadoEm', desc: true }]);
  const [searched, setSearched] = useState(false);

  const [closingIncident, setClosingIncident] = useState<IncidentRow | null>(null);
  const [deletingIncident, setDeletingIncident] = useState<IncidentRow | null>(null);

  // ── Form state ──────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [tenantError, setTenantError] = useState('');

  const {
    register,
    handleSubmit,
    reset: formReset,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: zodResolver(formSchema),
    defaultValues: { titulo: '', descricao: '', severidade: 'ALTA', tipo: 'FALHA' },
  });

  // Reset form when switching to form view
  useEffect(() => {
    if (viewMode === 'form') {
      if (editingIncident) {
        formReset({
          titulo: editingIncident.titulo,
          descricao: editingIncident.descricao ?? '',
          severidade: editingIncident.severidade,
          tipo: editingIncident.tipo,
        });
        setSelectedIds(editingIncident.tenants.map((t) => t.tenantId));
        setSelectAll(false);
      } else {
        formReset({ titulo: '', descricao: '', severidade: 'ALTA', tipo: 'FALHA' });
        setSelectedIds([]);
        setSelectAll(false);
      }
      setTenantError('');
    }
  }, [viewMode, editingIncident]);

  // ── Queries ───────────────────────────────────────────────
  const params = useMemo<IncidentListParams>(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      status: statusFilter === 'all' ? undefined : statusFilter,
      tenantId: tenantFilter || undefined,
      from: fromDate ? fromDate.toISOString() : undefined,
      to: toDate ? toDate.toISOString() : undefined,
      sortBy: (sorting[0]?.id as IncidentListParams['sortBy']) ?? 'iniciadoEm',
      sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
    }),
    [pagination.pageIndex, pagination.pageSize, statusFilter, tenantFilter, fromDate, toDate, sorting],
  );

  const { data: response, isLoading } = useIncidentList(params, searched);
  const { data: activeTenants } = useActiveTenants();

  const createMutation = useCreateIncident(() => { setViewMode('list'); setEditingIncident(null); });
  const updateMutation = useUpdateIncident(() => { setViewMode('list'); setEditingIncident(null); });
  const closeMutation = useCloseIncident(() => setClosingIncident(null));
  const deleteMutation = useDeleteIncident(() => setDeletingIncident(null));

  // ── Tenant handlers ─────────────────────────────────────
  function handleToggle(id: string) {
    setTenantError('');
    if (selectAll) {
      setSelectAll(false);
      setSelectedIds((activeTenants ?? []).filter((t) => t.id !== id).map((t) => t.id));
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    }
  }

  function handleSelectAll() {
    setTenantError('');
    setSelectAll(true);
    setSelectedIds([]);
  }

  function handleClearAll() {
    setSelectAll(false);
    setSelectedIds([]);
  }

  function onFormSubmit(data: FormFields) {
    if (!selectAll && selectedIds.length === 0) {
      setTenantError('Selecione ao menos um tenant');
      return;
    }

    if (editingIncident) {
      const originalTenantIds = editingIncident.tenants.map((t) => t.tenantId);
      const addTenantIds = selectedIds.filter((id) => !originalTenantIds.includes(id));
      const removeTenantIds = originalTenantIds.filter((id) => !selectedIds.includes(id));

      const input: UpdateIncidentDTO = {
        titulo: data.titulo,
        descricao: data.descricao || undefined,
        severidade: data.severidade,
        tipo: data.tipo,
        ...(addTenantIds.length > 0 ? { addTenantIds } : {}),
        ...(removeTenantIds.length > 0 ? { removeTenantIds } : {}),
      };

      updateMutation.mutate({ id: editingIncident.id, input });
    } else {
      const payload: CreateIncidentDTO = {
        titulo: data.titulo,
        descricao: data.descricao || undefined,
        severidade: data.severidade,
        tipo: data.tipo,
      };

      if (selectAll) {
        payload.selectAll = true;
      } else {
        payload.tenantIds = selectedIds;
      }

      createMutation.mutate(payload);
    }
  }

  function handleSearch() {
    setSearched(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  function handleClear() {
    setStatusFilter('all');
    setTenantFilter('');
    setFromDate(null);
    setToDate(null);
    setSorting([{ id: 'iniciadoEm', desc: true }]);
    setSearched(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  function handleEdit(incident: IncidentRow) {
    setEditingIncident(incident);
    setViewMode('form');
  }

  // ── Columns ───────────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'actions',
        header: 'Ações',
        enableSorting: false,
        cell: ({ row }) => {
          const incident = row.original;
          const isOpen = !incident.encerradoEm;
          const actions: RowAction[] = [];

          if (isOpen) {
            actions.push({
              label: 'Editar',
              icon: <Pencil size={14} />,
              onClick: () => handleEdit(incident),
            });
            actions.push({
              label: 'Fechar',
              icon: <XCircle size={14} />,
              onClick: () => setClosingIncident(incident),
            });
          } else {
            actions.push({
              label: 'Visualizar',
              icon: <Eye size={14} />,
              onClick: () => handleEdit(incident),
            });
          }

          if (incident.tenants.length > 0) {
            const firstTenant = incident.tenants[0];
            const monitorUrl = `${window.location.origin}/uptime?host=${firstTenant.slug}.woli.com.br`;
            actions.push({
              label: incident.tenants.length === 1
                ? 'Ver Monitoramento'
                : `Ver Monitoramento (${firstTenant.nome})`,
              icon: <Monitor size={14} />,
              onClick: () => window.open(monitorUrl, '_blank'),
            });
          }

          actions.push({
            label: 'Excluir',
            icon: <Trash2 size={14} />,
            variant: 'danger',
            onClick: () => setDeletingIncident(incident),
          });

          return <RowActionMenu actions={actions} />;
        },
      }),
      columnHelper.accessor('titulo', {
        header: 'Título',
        cell: (info) => (
          <div className="max-w-[200px]">
            <span className="font-medium">{info.getValue()}</span>
            {!info.row.original.encerradoEm && (
              <span className="ml-1.5 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium" style={{ background: 'var(--color-status-down)', color: '#fff' }}>
                ABERTO
              </span>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('tenants', {
        header: 'Tenants Afetados',
        enableSorting: false,
        cell: (info) => {
          const tenants = info.getValue();
          if (tenants.length === 0) return <span className="text-gray-400">—</span>;
          const show = tenants.slice(0, 3);
          const rest = tenants.length - show.length;
          return (
            <div className="flex flex-wrap gap-1">
              {show.map((t) => (
                <span
                  key={t.tenantId}
                  className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                >
                  {t.nome}
                </span>
              ))}
              {rest > 0 && (
                <span className="inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-600 dark:text-gray-400">
                  +{rest}
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('severidade', {
        header: 'Severidade',
        cell: (info) => {
          const val = info.getValue();
          return (
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${SEVERIDADE_COLORS[val] ?? ''}`}>
              {SEVERIDADE_LABELS[val] ?? val}
            </span>
          );
        },
      }),
      columnHelper.accessor('tipo', {
        header: 'Tipo',
        cell: (info) => (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {TIPO_LABELS[info.getValue()] ?? info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('iniciadoEm', {
        header: 'Início (GMT-3)',
        cell: (info) => <span className="text-xs">{fmtDate(info.getValue())}</span>,
      }),
      columnHelper.accessor('encerradoEm', {
        header: 'Fim (GMT-3)',
        cell: (info) => {
          const val = info.getValue();
          const causaRaiz = info.row.original.causaRaiz;
          return (
            <span className="inline-flex items-center gap-1.5 text-xs">
              {fmtDate(val)}
              {val && causaRaiz && <CausaRaizTooltip causaRaiz={causaRaiz} />}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'duracao',
        header: 'Duração',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {calcDuration(row.original.iniciadoEm, row.original.encerradoEm)}
          </span>
        ),
      }),
    ],
    [],
  );

  // ── Table ─────────────────────────────────────────────────
  const table = useReactTable({
    data: response?.data ?? [],
    columns,
    pageCount: response ? Math.ceil(response.total / response.limit) : -1,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  // ── VIEW: Formulário ──────────────────────────────────────
  if (viewMode === 'form') {
    const isEditing = !!editingIncident;
    const isViewOnly = !!editingIncident?.encerradoEm;
    const isPending = isEditing ? updateMutation.isPending : createMutation.isPending;
    const formTitle = isViewOnly ? 'Visualizar Incidente' : isEditing ? 'Editar Incidente' : 'Novo Incidente';
    const disabledClass = 'cursor-not-allowed !bg-gray-100 dark:!bg-gray-700 opacity-70';

    return (
      <div className="space-y-4">
        <button
          onClick={() => { setViewMode('list'); setEditingIncident(null); }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="border-b-2 border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-xl font-semibold">{formTitle}</h2>
          </div>
          <div className="space-y-5 px-6 py-6">
            {/* Titulo */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">Título</label>
              <input
                {...register('titulo')}
                disabled={isViewOnly}
                className={`w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 ${isViewOnly ? disabledClass : ''}`}
                placeholder="Descreva o incidente"
              />
              {errors.titulo && <p className="mt-1 text-xs text-red-500">{errors.titulo.message}</p>}
            </div>

            {/* Descricao */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">Descrição <span className="text-gray-400">(opcional)</span></label>
              <textarea
                {...register('descricao')}
                disabled={isViewOnly}
                rows={3}
                className={`w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 ${isViewOnly ? disabledClass : ''}`}
                placeholder="Detalhes adicionais..."
              />
            </div>

            {/* Severidade + Tipo */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1.5 flex items-center">
                  <label className="text-sm font-medium">Severidade</label>
                  <FieldTooltip
                    title="Severidade"
                    subtitle="Nível de impacto do incidente"
                    details={[
                      { text: 'Define a urgência e prioridade do tratamento do incidente.' },
                      { bold: 'Crítica:', text: 'falha total, todos os usuários afetados. Requer causa raiz ao encerrar.' },
                      { bold: 'Alta:', text: 'impacto significativo, maioria dos serviços comprometida.' },
                      { bold: 'Média:', text: 'degradação parcial, funcionalidades secundárias afetadas.' },
                    ]}
                    tip="Incidentes com severidade Crítica exigem preenchimento da causa raiz para encerramento."
                  />
                </div>
                <select
                  {...register('severidade')}
                  disabled={isViewOnly}
                  className={`w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 ${isViewOnly ? disabledClass : ''}`}
                >
                  <option value="CRITICA">Crítica</option>
                  <option value="ALTA">Alta</option>
                  <option value="MEDIA">Média</option>
                </select>
              </div>
              <div>
                <div className="mb-1.5 flex items-center">
                  <label className="text-sm font-medium">Tipo</label>
                  <FieldTooltip
                    title="Tipo"
                    subtitle="Categoria do incidente"
                    details={[
                      { text: 'Classifica a natureza do problema identificado.' },
                      { bold: 'Falha:', text: 'sistema completamente inacessível ou fora do ar (DOWN).' },
                      { bold: 'Degradação:', text: 'sistema respondendo com lentidão ou erros intermitentes.' },
                    ]}
                    tip="Utilize Falha quando o sistema não responde e Degradação quando responde parcialmente."
                  />
                </div>
                <select
                  {...register('tipo')}
                  disabled={isViewOnly}
                  className={`w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 ${isViewOnly ? disabledClass : ''}`}
                >
                  <option value="FALHA">Falha</option>
                  <option value="DEGRADACAO">Degradação</option>
                </select>
              </div>
            </div>

            {/* Datas (somente visualização) */}
            {isViewOnly && editingIncident && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Início (GMT-3)</label>
                  <div className="rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {fmtDate(editingIncident.iniciadoEm)}
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Encerrado em (GMT-3)</label>
                  <div className="rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {fmtDate(editingIncident.encerradoEm)}
                  </div>
                </div>
              </div>
            )}

            {/* Causa Raiz (somente visualização) */}
            {isViewOnly && editingIncident?.causaRaiz && (
              <div>
                <label className="mb-1.5 block text-sm font-medium">Causa Raiz</label>
                <div className="rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm whitespace-pre-wrap dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {editingIncident.causaRaiz}
                </div>
              </div>
            )}

            {/* TenantMultiSelect */}
            {!isViewOnly && (
              <>
                <TenantMultiSelect
                  tenants={activeTenants ?? []}
                  selected={selectedIds}
                  selectAll={selectAll}
                  onToggle={handleToggle}
                  onSelectAll={handleSelectAll}
                  onClearAll={handleClearAll}
                />
                {tenantError && <p className="text-xs text-red-500">{tenantError}</p>}
              </>
            )}

            {/* Tenants afetados (somente visualização) */}
            {isViewOnly && editingIncident && editingIncident.tenants.length > 0 && (
              <div>
                <label className="mb-1.5 block text-sm font-medium">Tenants Afetados</label>
                <div className="flex flex-wrap gap-1.5">
                  {editingIncident.tenants.map((t) => (
                    <span
                      key={t.tenantId}
                      className="inline-flex rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                    >
                      {t.nome}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
              <button
                type="button"
                onClick={() => { setViewMode('list'); setEditingIncident(null); }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {isViewOnly ? 'Voltar' : 'Cancelar'}
              </button>
              {!isViewOnly && (
                <button
                  type="button"
                  onClick={handleSubmit(onFormSubmit)}
                  disabled={isPending}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isPending
                    ? (isEditing ? 'Salvando...' : 'Criando...')
                    : (isEditing ? 'Salvar Alterações' : 'Criar Incidente')
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── VIEW: Lista ───────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Incidentes</h1>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            Registre e acompanhe incidentes de indisponibilidade dos tenants
            <button
              onClick={() => setShowHelp(true)}
              className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
              title="Como funcionam os incidentes?"
            >
              <HelpCircle size={16} />
            </button>
          </p>
        </div>
        <button
          onClick={() => { setEditingIncident(null); setViewMode('form'); }}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus size={16} />
          Novo Incidente
        </button>
      </div>

      {/* Painel de filtros */}
      <div className="rounded border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Tenant</label>
            <TenantCombobox
              value={tenantFilter}
              onChange={(id) => {
                setTenantFilter(id);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">De</label>
            <DateOnlyPicker
              value={fromDate}
              onChange={(d) => setFromDate(d)}
              maxDate={toDate ?? undefined}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Até</label>
            <DateOnlyPicker
              value={toDate}
              onChange={(d) => setToDate(d)}
              minDate={fromDate ?? undefined}
            />
          </div>
          <div className="flex flex-col items-end">
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
            <div className="flex items-center gap-4 pt-1">
              {([['all', 'Todos'], ['open', 'Abertos'], ['closed', 'Fechados']] as const).map(([value, label]) => (
                <label key={value} className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="incidentStatusFilter"
                    value={value}
                    checked={statusFilter === value}
                    onChange={() => {
                      setStatusFilter(value as StatusFilter);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                    className="h-4 w-4 cursor-pointer accent-blue-600"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Ações: Pesquisar + Limpar */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            title="Utilize os filtros para buscar e filtrar os incidentes"
          >
            <Search size={16} />
            Pesquisar
          </button>
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 rounded-md bg-gray-400 px-4 py-2 text-sm font-medium text-white hover:bg-gray-500"
          >
            <Eraser size={16} />
            Limpar
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        {!searched ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <Search size={24} className="mb-1" />
            <p className="text-sm font-medium">Utilize os filtros acima e clique em Pesquisar</p>
          </div>
        ) : isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : response && response.data.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <p className="text-sm font-medium">Nenhum incidente encontrado.</p>
            <p className="text-xs">Adicione novos incidentes ou ajuste os filtros de busca.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b-2 border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    return (
                      <th
                        key={header.id}
                        className={`px-4 py-3 ${canSort ? 'cursor-pointer select-none' : ''}`}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            header.column.getIsSorted() === 'asc'
                              ? <ArrowUpNarrowWide size={14} className="text-blue-600 dark:text-blue-400" />
                              : header.column.getIsSorted() === 'desc'
                                ? <ArrowDownWideNarrow size={14} className="text-blue-600 dark:text-blue-400" />
                                : <ArrowUpDown size={14} className="text-gray-300 dark:text-gray-600" />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={`border-b border-gray-100 transition-colors hover:bg-blue-50/30 dark:border-gray-800 dark:hover:bg-blue-900/10 ${Number(row.id) % 2 === 1 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Paginação */}
        {searched && response && response.total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
            <span className="text-gray-500 dark:text-gray-400">
              Mostrando {Math.min(pagination.pageIndex * pagination.pageSize + 1, response.total)} a{' '}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, response.total)} de {response.total} registros
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <span className="text-xs">Resultados por página:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })}
                  className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Anterior
                </button>
                <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                  {pagination.pageIndex + 1} / {table.getPageCount()}
                </span>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de fechamento */}
      {closingIncident && (
        <CloseIncidentModal
          incident={closingIncident}
          onClose={() => setClosingIncident(null)}
          onConfirm={(causaRaiz) =>
            closeMutation.mutate({ id: closingIncident.id, input: { causaRaiz } })
          }
          isSubmitting={closeMutation.isPending}
        />
      )}

      {/* Modal de exclusão */}
      {deletingIncident && (
        <ConfirmModal
          title="Excluir Incidente"
          message={`Tem certeza que deseja excluir o incidente "${deletingIncident.titulo}"? Esta ação é irreversível.`}
          confirmLabel="Excluir"
          onConfirm={() => deleteMutation.mutate(deletingIncident.id)}
          onCancel={() => setDeletingIncident(null)}
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Modal de ajuda */}
      {showHelp && <IncidentHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}
